import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { hasPermission } from '../lib/permissions';

/**
 * usePayrollTrackingContext — loads payroll connections + tracking data
 * when per_block_tracking_export flag is on and the user has payroll.read.
 *
 * Returns { platform, connectionId, connections, trackingCategories,
 *           timesheetCategoryId, myobJobs, loading }
 */
export function usePayrollTrackingContext() {
  const { api, user } = useAuth();
  const { isEnabled } = useFeatureFlags();

  const flagOn = isEnabled('per_block_tracking_export');
  const canRead = hasPermission(user, 'payroll.read');
  const enabled = flagOn && canRead;

  const [connections, setConnections] = useState([]);
  const [trackingCategories, setTrackingCategories] = useState([]);
  const [timesheetCategoryId, setTimesheetCategoryId] = useState(null);
  const [myobJobs, setMyobJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedConnectionId, setSelectedConnectionId] = useState(null);

  // Load accounting connections
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/accounting/connections');
        const conns = (res.data?.connections || res.data || [])
          .filter(c => ['xero', 'myob'].includes(c.provider) && c.status !== 'disconnected');
        if (!cancelled) {
          setConnections(conns);
          if (conns.length === 1) setSelectedConnectionId(conns[0].id);
        }
      } catch (e) {
        console.warn('Failed to load accounting connections for tracking context', e);
      }
    })();
    return () => { cancelled = true; };
  }, [api, enabled]);

  // Derive platform from connections
  const platform = useMemo(() => {
    if (!enabled || connections.length === 0) return null;
    const active = selectedConnectionId
      ? connections.find(c => c.id === selectedConnectionId)
      : connections[0];
    return active?.provider || null;
  }, [enabled, connections, selectedConnectionId]);

  const connectionId = useMemo(() => {
    if (!enabled) return null;
    if (selectedConnectionId) return selectedConnectionId;
    if (connections.length === 1) return connections[0].id;
    return null;
  }, [enabled, connections, selectedConnectionId]);

  // Load tracking data based on platform and connectionId
  useEffect(() => {
    if (!enabled || !platform || !connectionId) {
      setTrackingCategories([]);
      setTimesheetCategoryId(null);
      setMyobJobs([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const connQ = `?connection_id=${connectionId}`;
        if (platform === 'xero') {
          const res = await api.get(`/payroll/xero/tracking-categories${connQ}`);
          if (!cancelled) {
            setTrackingCategories(res.data?.tracking_categories || []);
            setTimesheetCategoryId(res.data?.timesheet_category_id || null);
            setMyobJobs([]);
          }
        } else if (platform === 'myob') {
          const res = await api.get(`/payroll/myob/jobs${connQ}`);
          if (!cancelled) {
            setMyobJobs(res.data?.jobs || []);
            setTrackingCategories([]);
            setTimesheetCategoryId(null);
          }
        }
      } catch (e) {
        console.warn(`Failed to load ${platform} tracking data`, e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, enabled, platform, connectionId]);

  return {
    platform: enabled ? platform : null,
    connectionId: enabled ? connectionId : null,
    connections: enabled ? connections : [],
    trackingCategories,
    timesheetCategoryId,
    myobJobs,
    loading,
    flagOn,
    setSelectedConnectionId,
  };
}
